/**
 * EncounterSlice Selectors
 * 
 * Memoized selectors for accessing encounter slice state.
 * Uses createSelector for performance optimization.
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { EncounterSliceState } from './state';

// Base selector to get the encounter slice
const selectEncounterSlice = (state: RootState): EncounterSliceState => state.encounter;

// =============================================================================
// CORE SELECTORS
// =============================================================================

/**
 * Select the current encounter entity
 */
export const selectCurrentEncounter = createSelector(
  selectEncounterSlice,
  (slice) => slice.currentEncounter
);

/**
 * Select whether current encounter exists
 */
export const selectHasCurrentEncounter = createSelector(
  selectCurrentEncounter,
  (encounter) => encounter !== null
);

/**
 * Select the encounter ID (if exists)
 */
export const selectEncounterId = createSelector(
  selectCurrentEncounter,
  (encounter) => encounter?.id
);

// =============================================================================
// EDIT TRACKING SELECTORS
// =============================================================================

/**
 * Select whether any edits have been made
 */
export const selectEncounterIsEdited = createSelector(
  selectEncounterSlice,
  (slice) => slice.isEdited
);

/**
 * Select the list of edited field names
 */
export const selectEncounterEditedFields = createSelector(
  selectEncounterSlice,
  (slice) => slice.editedFields
);

/**
 * Factory to create a selector for checking if a specific field is edited
 */
export const selectIsEncounterFieldEdited = (fieldName: string) =>
  createSelector(selectEncounterEditedFields, (fields) => fields.includes(fieldName));

// =============================================================================
// VALIDATION SELECTORS
// =============================================================================

/**
 * Select all validation errors
 */
export const selectEncounterValidationErrors = createSelector(
  selectEncounterSlice,
  (slice) => slice.validationErrors
);

/**
 * Select whether there are any validation errors
 */
export const selectHasEncounterValidationErrors = createSelector(
  selectEncounterValidationErrors,
  (errors) => Object.keys(errors).length > 0
);

/**
 * Factory to create a selector for a specific field's validation error
 */
export const selectEncounterValidationError = (fieldName: string) =>
  createSelector(selectEncounterValidationErrors, (errors) => errors[fieldName]);

// =============================================================================
// SUB-ENTITY SELECTORS
// =============================================================================

/**
 * Select encounter procedures
 */
export const selectEncounterProcedures = createSelector(
  selectCurrentEncounter,
  (encounter) => encounter?.procedures ?? []
);

/**
 * Select procedures sorted by priority
 */
export const selectEncounterProceduresSorted = createSelector(
  selectEncounterProcedures,
  (procedures) => [...procedures].sort((a, b) => (a.priority || 0) - (b.priority || 0))
);

/**
 * Select procedures count
 */
export const selectEncounterProceduresCount = createSelector(
  selectEncounterProcedures,
  (procedures) => procedures.length
);

/**
 * Select encounter insurances
 */
export const selectEncounterInsurances = createSelector(
  selectCurrentEncounter,
  (encounter) => encounter?.insurances ?? []
);

/**
 * Select insurances sorted by priority
 */
export const selectEncounterInsurancesSorted = createSelector(
  selectEncounterInsurances,
  (insurances) => [...insurances].sort((a, b) => (a.priority || 0) - (b.priority || 0))
);

/**
 * Select insurances count
 */
export const selectEncounterInsurancesCount = createSelector(
  selectEncounterInsurances,
  (insurances) => insurances.length
);

/**
 * Select encounter scheduled procedure codes
 */
export const selectEncounterScheduledProcedureCodes = createSelector(
  selectCurrentEncounter,
  (encounter) => encounter?.scheduledProcedureCodes ?? []
);

/**
 * Select scheduled procedure codes sorted by priority
 */
export const selectEncounterScheduledProcedureCodesSorted = createSelector(
  selectEncounterScheduledProcedureCodes,
  (codes) => [...codes].sort((a, b) => (a.priority || 0) - (b.priority || 0))
);

/**
 * Select scheduled procedure codes count
 */
export const selectEncounterScheduledProcedureCodesCount = createSelector(
  selectEncounterScheduledProcedureCodes,
  (codes) => codes.length
);

// =============================================================================
// DERIVED SELECTORS
// =============================================================================

/**
 * Select computed data about the encounter
 */
export const selectEncounterSummary = createSelector(
  selectCurrentEncounter,
  selectEncounterIsEdited,
  selectEncounterProceduresCount,
  selectEncounterInsurancesCount,
  (encounter, isEdited, proceduresCount, insurancesCount) => ({
    id: encounter?.id,
    patientName: encounter?.patient
      ? `${encounter.patient.lastName}, ${encounter.patient.firstName}`
      : undefined,
    isEdited,
    proceduresCount,
    insurancesCount,
  })
);

/**
 * Select primary procedure (priority = 1)
 */
export const selectPrimaryProcedure = createSelector(
  selectEncounterProcedures,
  (procedures) => procedures.find((p) => p.isPrimary || p.priority === 1)
);

/**
 * Select primary insurance (priority = 1)
 */
export const selectPrimaryInsurance = createSelector(
  selectEncounterInsurances,
  (insurances) => insurances.find((i) => i.priority === 1)
);
